import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContactForm from '../components/ContactForm';
import PublicPageShell from '../components/PublicPageShell';

const PRIMARY = '#0087c1';

export default function Contact() {
  const theme = useTheme();

  return (
    <PublicPageShell>
      <Helmet>
        <title>Contact Us — Get a Demo of Struxor Construction Software</title>
        <meta
          name="description"
          content="Get in touch with the Struxor team. Request a demo, ask a question, or tell us about your construction software needs. We'd love to hear from you."
        />
        <link rel="canonical" href="https://www.struxorapp.com/contact" />
        <meta property="og:title" content="Contact Us — Get a Demo of Struxor Construction Software" />
        <meta property="og:description" content="Get in touch with the Struxor team. Request a demo, ask a question, or tell us about your construction software needs." />
        <meta property="og:url" content="https://www.struxorapp.com/contact" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Struxor',
          description: 'Get in touch with the Struxor team for demos, support, or general enquiries.',
          url: 'https://www.struxorapp.com/contact',
          isPartOf: { '@type': 'WebSite', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Get in touch
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 550, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            Have a question, want a demo, or just want to say hello? We'd love to hear from you.
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={5}>
          {/* Contact form */}
          <Grid item xs={12} md={7}>
            <Typography variant="h5" component="h2" fontWeight={700} mb={1}>
              Send us a message
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Whether you're interested in a demo, have a technical question, or want to discuss enterprise requirements — drop us a line and we'll get back to you quickly.
            </Typography>
            <ContactForm
              onSuccess={(data) => {
                console.log('Contact form submitted:', data);
              }}
            />
          </Grid>

          {/* Sidebar info */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {[
                {
                  icon: AccessTimeIcon,
                  title: 'Response time',
                  text: 'We aim to respond to all enquiries within one business day.',
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={item.title}
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          background: alpha(PRIMARY, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: PRIMARY, fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Trial CTA card */}
              <Card
                elevation={0}
                sx={{
                  background: alpha(PRIMARY, 0.04),
                  border: `1px solid ${alpha(PRIMARY, 0.2)}`,
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>
                    Want to try it yourself?
                  </Typography>
                  <Stack spacing={1} mb={2}>
                    {[
                      '14-day free trial',
                      'Unlimited users and projects',
                      'No credit card required',
                    ].map((b) => (
                      <Stack direction="row" spacing={1} alignItems="center" key={b}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 16, color: PRIMARY }} />
                        <Typography variant="body2" color="text.secondary">{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="contained"
                    fullWidth
                    sx={{
                      background: PRIMARY,
                      '&:hover': { background: '#006fa0' },
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </PublicPageShell>
  );
}
